import { useEffect, useState, useCallback } from 'react';
import { dviApi } from '../services/api/dviApi';
import { socketService } from '../services/socketService';
import {
  DVIInspectionFull, DVIItemStatus,
} from '../types/dvi';

interface ItemUpdatedPayload {
  itemId: number;
  status: DVIItemStatus;
  notes: string | null;
  hasPhoto: boolean;
}

interface CompletedPayload {
  inspectionId: number;
  counts: { pass: number; attention: number; fail: number };
}

interface LineItemAddedPayload {
  itemId: number;
  lineItemId: number;
  lineItemType: 'estimate' | 'work_order';
}

export interface UseDVIInspection {
  inspection: DVIInspectionFull | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateItem: (itemId: number, input: {
    status: Exclude<DVIItemStatus, 'pending'>; notes?: string | null;
  }) => Promise<void>;
  complete: () => Promise<void>;
}

export function useDVIInspection(workOrderId: number): UseDVIInspection {
  const [inspection, setInspection] = useState<DVIInspectionFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOnce = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await dviApi.get(workOrderId);
      setInspection(r);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load DVI');
    } finally {
      setLoading(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    fetchOnce();
    const room = `wo:${workOrderId}`;
    socketService.joinRoom(room);

    const onItemUpdated = (data: ItemUpdatedPayload) => {
      setInspection(prev => {
        if (!prev) return prev;
        const items = prev.items.map(i =>
          i.id === data.itemId
            ? { ...i, status: data.status, notes: data.notes }
            : i,
        );
        return { ...prev, items };
      });
    };
    const onCompleted = (data: CompletedPayload) => {
      setInspection(prev => prev && prev.id === data.inspectionId
        ? { ...prev, status: 'completed' as const }
        : prev);
    };
    const onLineItemAdded = (data: LineItemAddedPayload) => {
      setInspection(prev => {
        if (!prev) return prev;
        const items = prev.items.map(i =>
          i.id === data.itemId
            ? { ...i, line_item_id: data.lineItemId, line_item_type: data.lineItemType }
            : i,
        );
        return { ...prev, items };
      });
    };

    socketService.on('dvi:item-updated', onItemUpdated);
    socketService.on('dvi:completed', onCompleted);
    socketService.on('dvi:line-item-added', onLineItemAdded);

    return () => {
      socketService.off('dvi:item-updated');
      socketService.off('dvi:completed');
      socketService.off('dvi:line-item-added');
      socketService.leaveRoom(room);
    };
  }, [workOrderId, fetchOnce]);

  const updateItem = useCallback(async (itemId: number, input: {
    status: Exclude<DVIItemStatus, 'pending'>; notes?: string | null;
  }) => {
    if (!inspection) return;
    // Optimistic update
    setInspection(prev => prev && {
      ...prev,
      items: prev.items.map(i => i.id === itemId
        ? { ...i, status: input.status, notes: input.notes ?? i.notes }
        : i),
    });
    try {
      await dviApi.updateItem(inspection.id, itemId, input);
    } catch (e) {
      // Rollback on failure
      await fetchOnce();
      throw e;
    }
  }, [inspection, fetchOnce]);

  const complete = useCallback(async () => {
    if (!inspection) return;
    await dviApi.complete(inspection.id);
    await fetchOnce();
  }, [inspection, fetchOnce]);

  return { inspection, loading, error, refetch: fetchOnce, updateItem, complete };
}
